import { EventEmitter } from 'eventemitter3';

import { createDeferredPromise } from '../../utils/deferredPromise';
import { CannotTrackError } from './../../errors/CannotTrackError';
import type {
  AsyncProcessEntry,
  AsyncTrack,
  AsyncTrackEmitter,
} from './asyncTrack.types';

/**
 * Creates a tracker that associates names with promises and raises as promises resolve.
 */
export const createAsyncTrack = (): AsyncTrack => {
  /**
   * Whether tracking is enabled.
   * All processes must be tracked on the same node tick.
   *
   * This ensures no process is missed (as it would throw when tracked on a different node tick).
   */
  let isTrackEnabled = true;

  const eventEmitter: AsyncTrackEmitter = new EventEmitter();

  const allSettledPromise = createDeferredPromise<void>();

  const tracker = new Map<string, AsyncProcessEntry>();

  /**
   * Checks if all processes are initialized and resolves the main promise if they are.
   */
  const checkIfAllSettled = () => {
    const allSettled = Array.from(tracker.values()).every(
      ({ status }) => status === 'resolved'
    );

    if (!allSettled) return;

    allSettledPromise.resolve();
  };

  const track: AsyncTrack['track'] = ({ name, promise }) => {
    if (!isTrackEnabled) {
      throw new CannotTrackError();
    }

    // Disable tracking in the next node tick
    void Promise.resolve().then(() => (isTrackEnabled = false));

    tracker.set(name, { promise, status: 'pending' });

    promise
      .then(() => {
        tracker.set(name, { promise, status: 'resolved' });

        eventEmitter.emit('resolved', name);

        checkIfAllSettled();
      })
      .catch((error) => {
        tracker.set(name, { promise, status: 'failed' });

        eventEmitter.emit('failed', name, error);

        allSettledPromise.reject(error);
      });
  };

  const waitForAll: AsyncTrack['waitForAll'] = () => allSettledPromise.promise;

  const getTracker: AsyncTrack['getTracker'] = <T>(trackerKey: string) =>
    tracker.get(trackerKey) as AsyncProcessEntry<T>;

  return {
    getTracker,
    off: eventEmitter.off.bind(eventEmitter),
    on: eventEmitter.on.bind(eventEmitter),
    track,
    waitForAll,
  };
};
